import { useState } from 'react';
import { FaPen, FaPlus, FaTrash, FaCircleCheck, FaCircleHalfStroke } from 'react-icons/fa6';
import CurrencyInput from '../../../molecules/CurrencyInput';
import TextEdit from '../../../molecules/TextEdit';
import useLegalCaseClaim from '../../../../hooks/useLegalCaseClaim';
import useLegalCaseClaimForm from '../../../../hooks/useLegalCaseClaimForm';
import type { Claim, ClaimItem } from '../../../../types/LegalCase';
import LegalCaseClaimItemForm from './LegalCaseClaimItemForm';
import ClaimChargesCard from './ClaimChargesCard';
import ClaimCompositionBar from './ClaimCompositionBar';
import ConfirmDialog from './ConfirmDialog';
import { SectionHelpTitle } from './SectionHelp';
import { dueBadge, dueBadgeClass } from './claimItemDueStatus';

interface LegalCaseClaimProps {
    caseId: string;
    initialClaim?: Claim | null;
}

const Row = ({ label, value, strong }: { label: string; value: string | null; strong?: boolean }) => (
    <div className="flex items-center justify-between text-sm">
        <span className="text-gray-500 dark:text-gray-400">{label}</span>
        <span className={strong ? 'font-semibold text-gray-900 dark:text-white' : 'dark:text-white'}>
            {value || '—'}
        </span>
    </div>
);

// Linha de juros/multa no card "Valores da petição". Quando o valor NÃO foi digitado (override),
// mostra o **calculado** pela taxa do caso com a marca "(calculado)" — senão a linha fica em branco
// e dá a impressão de que não há encargo, quando na verdade ele existe e está no card ao lado. O
// valor calculado não entra no "Total da pretensão" (que é o ajuizado); o total com encargo é o
// "Valor atualizado".
const ChargeRow = ({
    label,
    entered,
    enteredFormatted,
    calculatedFormatted,
}: {
    label: string;
    entered: number | null;
    enteredFormatted: string | null;
    calculatedFormatted: string | null;
}) => {
    if (entered != null) return <Row label={label} value={enteredFormatted} />;
    if (calculatedFormatted) {
        return (
            <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{label}</span>
                <span className="text-gray-500 dark:text-gray-400">
                    {calculatedFormatted}{' '}
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">(calculado)</span>
                </span>
            </div>
        );
    }
    return <Row label={label} value={null} />;
};

// Caso legado: principal digitado antes dos títulos, e diferente da soma deles. O valor fica
// guardado, mas é ignorado — a tela avisa em vez de sumir com ele em silêncio.
const hasIgnoredEnteredPrincipal = (claim: Claim): boolean =>
    !!claim.principalFromItems
    && claim.principalEntered != null
    && claim.principalEntered !== claim.principal;

// Campos de valor da pretensão. Compartilhados pela criação e pela edição (AF-3691) — eram
// duplicados no JSX quando só existia a criação. `form` é o retorno de useLegalCaseClaimForm.
type ClaimValuesFormState = ReturnType<typeof useLegalCaseClaimForm>;

// `showOverrides`: Juros e Multa só aparecem na EDIÇÃO (Task 17). Num caso novo, o esperado é
// cadastrar a **taxa** no caso e deixar o sistema calcular — pedir "Juros" e "Multa" em reais na
// criação sugere que o usuário digita o encargo à mão, o que contradiz o cálculo. Na edição eles
// seguem como **override** (valor fixo que sobrepõe o cálculo), para o caso de decisão judicial
// que fixou o valor e para a carga da Task 15; por isso o rótulo diz "sobrepõe o cálculo".
// Principal calculado (há títulos): mostrado só para leitura. Os títulos SÃO o principal
// detalhado (ADR 005, decisão 6) — digitar outro valor seria afirmar uma dívida que os títulos
// não sustentam. O estado do form segue com o valor digitado guardado, que volta intacto no PUT.
const PrincipalFromItems = ({ formatted }: { formatted: string | null }) => (
    <div className="space-y-1">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Principal</p>
        <p className="text-sm dark:text-white">{formatted || '—'}</p>
        <p className="text-[11px] text-gray-400 dark:text-gray-500">
            Soma dos títulos cadastrados. Para alterar, edite os títulos.
        </p>
    </div>
);

const ClaimValuesFields = ({
    form,
    showOverrides = false,
    principalFromItems = false,
    principalFormatted = null,
}: {
    form: ClaimValuesFormState;
    showOverrides?: boolean;
    principalFromItems?: boolean;
    principalFormatted?: string | null;
}) => (
    <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {principalFromItems
                ? <PrincipalFromItems formatted={principalFormatted} />
                : <CurrencyInput label="Principal" prefix="R$" fractionDigits={2} value={form.principal} onChange={form.setPrincipal} />}
            <CurrencyInput label="Custas" prefix="R$" fractionDigits={2} value={form.legalCosts} onChange={form.setLegalCosts} />
            {showOverrides && (
                <>
                    <CurrencyInput label="Juros (valor fixo — sobrepõe o cálculo)" prefix="R$" fractionDigits={2} value={form.interest} onChange={form.setInterest} />
                    <CurrencyInput label="Multa (valor fixo — sobrepõe o cálculo)" prefix="R$" fractionDigits={2} value={form.penalties} onChange={form.setPenalties} />
                </>
            )}
        </div>
        {!showOverrides && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
                Juros e multa são calculados a partir das taxas do caso e aparecem no valor
                atualizado. Para fixar um valor manual, edite a pretensão depois de criá-la.
            </p>
        )}
        <TextEdit label="Observação" value={form.description} onChange={(e: any) => form.setDescription(e.target.value)} />
    </>
);

interface ItemLineProps {
    item: ClaimItem;
    onEdit: () => void;
    onRemove: () => void;
    disabled: boolean;
}

// Título vindo da cobrança (settlement): a cobrança é a dona do dado. Mesmo critério do backend
// (ADR 005, decisão 7): tipo TITLE_DEBT ou obligationId (o title_debt_id) preenchido. Editar ou
// excluir aqui induz a erro — o saldo e a situação chegam por evento, não se mexem à mão.
const isFromCollection = (item: ClaimItem): boolean =>
    item.obligationType === 'TITLE_DEBT' || !!item.obligationId;

const ItemLine = ({ item, onEdit, onRemove, disabled }: ItemLineProps) => {
    const due = dueBadge(item);
    const fromCollection = isFromCollection(item);
    return (
        <li className="flex items-start justify-between gap-2 rounded-lg bg-gray-50 dark:bg-slate-900 px-3 py-2">
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-mono dark:text-white">{item.titleNumber}</span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${item.isGuarantee
                        ? 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300'
                        : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300'}`}>
                        {item.businessReferenceFormatted}
                    </span>
                    {due && (
                        <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${dueBadgeClass(due.status)}`}
                            title={due.title}
                        >
                            {due.label}
                        </span>
                    )}
                    {item.externalReference && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {item.obligationTypeFormatted}: {item.externalReference}
                        </span>
                    )}
                    {item.settled && (
                        <span
                            className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300"
                            title="A cobrança deu este título por quitado"
                        >
                            Quitado na cobrança
                        </span>
                    )}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 flex items-center flex-wrap gap-x-1">
                    <span>{item.amountAtFilingFormatted}</span>
                    {item.paidAmount > 0 && (
                        <span className={`inline-flex items-center gap-1 ${item.remainingAmount <= 0
                            ? 'text-green-700 dark:text-green-300'
                            : 'text-gray-500 dark:text-gray-400'}`}>
                            {/* Verde e cheio quando quitado; contorno quando ainda há saldo. O ícone
                            destaca de relance quais títulos foram pagos, sem tirar o texto. */}
                            {item.remainingAmount <= 0
                                ? <FaCircleCheck size={11} className="shrink-0" />
                                : <FaCircleHalfStroke size={11} className="shrink-0" />}
                            <span>• pago {item.paidAmountFormatted} • saldo {item.remainingAmountFormatted}</span>
                        </span>
                    )}
                    {/* Task 24: confissão de dívida baixou o saldo sem entrar dinheiro. Fica à parte do
                    "pago" para não parecer que a Agrolend recebeu. */}
                    {!!item.confessedAmount && item.confessedAmount > 0 && (
                        <span className="text-gray-500 dark:text-gray-400">
                            • quitado por {item.settlementNatureFormatted || 'confissão'} {item.confessedAmountFormatted}
                        </span>
                    )}
                    {/* O desconto do acordo: o que a cobrança baixou além do que foi pago/confessado. */}
                    {!!item.settlementWriteOff && item.settlementWriteOff > 0 && (
                        <span className="text-gray-500 dark:text-gray-400">
                            • baixado na quitação {item.settlementWriteOffFormatted}
                        </span>
                    )}
                    {item.settlementNature === 'DEBT_CONFESSION' && item.settlementReferenceFormatted && (
                        <span className="text-gray-500 dark:text-gray-400">
                            • {item.settlementReferenceFormatted}
                        </span>
                    )}
                </p>
            </div>
            {fromCollection ? (
                <span
                    className="flex-shrink-0 inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                    title="Título gerido pela cobrança. Saldo e situação chegam por evento — não é editável aqui."
                >
                    Cobrança
                </span>
            ) : (
                <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                        onClick={onEdit}
                        disabled={disabled}
                        title="Editar título"
                        className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <FaPen size={11} />
                    </button>
                    <button
                        onClick={onRemove}
                        disabled={disabled}
                        title="Remover título"
                        className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <FaTrash size={11} />
                    </button>
                </div>
            )}
        </li>
    );
};

// Seção Pretensão (Claim) do detalhe do caso: valores da pretensão, lista de títulos com
// soma e saldo, e o formulário de adição. Garantia aparece à parte (fora do total). A regra
// anti-duplicação e o cálculo de totais vivem no backend — a tela só apresenta e coleta.
const LegalCaseClaim = ({ caseId, initialClaim }: LegalCaseClaimProps) => {
    const {
        claim, isSaving, errorFields,
        businessReferenceOptions, obligationTypeOptions,
        createClaim, updateClaim, addItem, updateItem, removeItem, clearErrors,
    } = useLegalCaseClaim(caseId, initialClaim);

    // Estado/handlers dos forms (criação, edição de valores e título) fora do componente —
    // ver useLegalCaseClaimForm.
    const form = useLegalCaseClaimForm({ createClaim, updateClaim, clearErrors });

    // Id do título pendente de confirmação de remoção (null = diálogo fechado).
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await removeItem(pendingRemoval);
        setPendingRemoval(null);
    };

    if (!claim && !form.creating) {
        return (
            <div className="space-y-3">
                <p className="text-sm text-gray-400 dark:text-gray-500">
                    Nenhuma pretensão cadastrada para este caso.
                </p>
                <button
                    onClick={form.openCreate}
                    className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                    <FaPlus size={12} /> Cadastrar pretensão
                </button>
            </div>
        );
    }

    if (!claim && form.creating) {
        return (
            <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
                <h4 className="text-sm font-medium dark:text-white">Nova pretensão</h4>
                <ClaimValuesFields form={form} />
                <div className="flex items-center justify-end gap-2">
                    <button onClick={form.cancelCreate} className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                        Cancelar
                    </button>
                    <button onClick={form.submitCreate} disabled={isSaving} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
                        {isSaving ? 'Salvando...' : 'Criar pretensão'}
                    </button>
                </div>
            </div>
        );
    }

    if (!claim) return null;

    return (
        <div className="space-y-4">
            <ClaimCompositionBar claim={claim} />

            {form.editing && (
                <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
                    <h4 className="text-sm font-medium dark:text-white">Editar pretensão</h4>
                    <ClaimValuesFields
                        form={form}
                        showOverrides
                        principalFromItems={!!claim.principalFromItems}
                        principalFormatted={claim.principalFormatted}
                    />
                    <div className="flex items-center justify-end gap-2">
                        <button onClick={form.cancelEdit} className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                            Cancelar
                        </button>
                        <button onClick={form.submitEdit} disabled={isSaving} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
                            {isSaving ? 'Salvando...' : 'Salvar pretensão'}
                        </button>
                    </div>
                </div>
            )}

            <div className="rounded-lg bg-gray-50 dark:bg-slate-900 p-3 space-y-1.5">
                {!form.editing && (
                    <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Valores da petição (como ajuizado)</h4>
                        <button
                            onClick={() => form.openEdit(claim)}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                        >
                            <FaPen size={10} /> Editar
                        </button>
                    </div>
                )}
                {/* Com títulos, o principal É a soma deles — sem a linha "Soma dos títulos" à
                    parte, que exibia o mesmo dinheiro duas vezes (ADR 005, decisão 6). */}
                <Row
                    label={claim.principalFromItems ? 'Principal (soma dos títulos)' : 'Principal'}
                    value={claim.principalFormatted}
                />
                {hasIgnoredEnteredPrincipal(claim) && (
                    <p className="text-[11px] text-amber-700 dark:text-amber-400">
                        O principal digitado ({claim.principalEnteredFormatted}) não entra na conta:
                        com títulos cadastrados, o principal é a soma deles.
                    </p>
                )}
                <ChargeRow
                    label="Juros"
                    entered={claim.interest}
                    enteredFormatted={claim.interestFormatted}
                    calculatedFormatted={claim.charges?.interestCalculatedFormatted ?? null}
                />
                <ChargeRow
                    label="Multa"
                    entered={claim.penalties}
                    enteredFormatted={claim.penaltiesFormatted}
                    calculatedFormatted={claim.charges?.penaltyCalculatedFormatted ?? null}
                />
                <Row label="Custas" value={claim.legalCostsFormatted} />
                <div className="border-t border-gray-200 dark:border-slate-700 my-1" />
                <Row label="Total da pretensão (sem encargos calculados)" value={claim.grandTotalFormatted} strong />
                {claim.guaranteeTotal > 0 && (
                    <Row label="Garantias (à parte)" value={claim.guaranteeTotalFormatted} />
                )}
                {(claim.paidTotal > 0 || (claim.settlementDischargedTotal ?? 0) > 0) && (
                    <>
                        <div className="border-t border-gray-200 dark:border-slate-700 my-1" />
                        <Row label="Pago" value={claim.paidTotalFormatted} />
                        {!!claim.unattributedPaidTotal && claim.unattributedPaidTotal > 0 && (
                            <Row
                                label="— sem atribuição a título"
                                value={claim.unattributedPaidTotalFormatted ?? null}
                            />
                        )}
                        {/* Task 24: baixado pela cobrança sem dinheiro (confissão + desconto na
                            quitação). Some do saldo como o pago, mas à parte — não foi recebido. */}
                        {!!claim.settlementDischargedTotal && claim.settlementDischargedTotal > 0 && (
                            <Row
                                label="Baixado na cobrança (sem pagamento)"
                                value={claim.settlementDischargedTotalFormatted ?? null}
                            />
                        )}
                        <Row label="Saldo remanescente" value={claim.remainingTotalFormatted} strong />
                    </>
                )}
            </div>

            {/* Encargos e valor atualizado (Task 17). Fica **depois** dos Valores de propósito: o
                bloco acima é a pretensão como registrada (o que vai na petição) e este é quanto a
                dívida vale hoje (o que se negocia) — os dois números do ADR 004, na ordem em que o
                jurídico raciocina. */}
            <ClaimChargesCard charges={claim.charges} />

            <div className="flex items-center justify-between">
                <SectionHelpTitle title="Títulos" topic="claim.items" />
                {!form.showItemForm && (
                    <button
                        onClick={form.openItemForm}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Adicionar
                    </button>
                )}
            </div>

            {claim.items.length === 0 && !form.showItemForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhum título cadastrado</p>
            )}

            <ul className="space-y-2">
                {claim.items.map((item) => (
                    <ItemLine
                        key={item.id}
                        item={item}
                        onEdit={() => form.openItemEdit(item)}
                        onRemove={() => setPendingRemoval(item.id)}
                        disabled={isSaving}
                    />
                ))}
            </ul>

            {form.showItemForm && (
                <LegalCaseClaimItemForm
                    businessReferenceOptions={businessReferenceOptions}
                    obligationTypeOptions={obligationTypeOptions}
                    errorFields={errorFields}
                    isSaving={isSaving}
                    onSubmit={addItem}
                    onCancel={form.closeItemForm}
                />
            )}

            {/* Edição de título: o mesmo form, semeado com o item. A `key` força remontagem ao
                trocar de título, para os campos re-semearem (o estado inicial só vale no mount). */}
            {form.editingItem && (
                <LegalCaseClaimItemForm
                    key={form.editingItem.id}
                    businessReferenceOptions={businessReferenceOptions}
                    obligationTypeOptions={obligationTypeOptions}
                    errorFields={errorFields}
                    isSaving={isSaving}
                    initialItem={form.editingItem}
                    onSubmit={(value) => updateItem(form.editingItem!.id, value)}
                    onCancel={form.closeItemForm}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover título"
                message="Remover este título da pretensão? Esta ação não pode ser desfeita."
                isBusy={isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseClaim;
